import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Lock, 
  Users, 
  Mail,
  Search,
  Filter,
  Reply,
  Archive,
  ArrowLeft
} from 'lucide-react';

interface SupportTicket {
  id: string;
  subject: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  category: 'account' | 'team' | 'chat' | 'technical';
  createdAt: string;
  user: {
    id: string;
    username: string;
    email: string;
    role: string;
  };
  responses?: SupportResponse[];
}

interface SupportResponse {
  id: string;
  message: string;
  isAdminResponse: boolean;
  createdAt: string;
  author: string;
}

interface SupportCenterProps {
  onBack?: () => void;
}

const SupportCenter: React.FC<SupportCenterProps> = ({ onBack }) => {
  const [tickets] = useState<SupportTicket[]>([
    {
      id: '1',
      subject: 'Cannot login to my account',
      message: 'I forgot my PIN and cannot access my account. Please help me reset it.',
      priority: 'high',
      status: 'open',
      category: 'account',
      createdAt: '2024-01-20T10:00:00Z',
      user: {
        id: 'user1',
        username: 'player123',
        email: 'player@example.com',
        role: 'player'
      }
    },
    {
      id: '2',
      subject: 'Team chat not working',
      message: 'Messages are not sending in our team chat group. Other team members are having the same issue.',
      priority: 'medium',
      status: 'in-progress',
      category: 'chat',
      createdAt: '2024-01-19T14:30:00Z',
      user: {
        id: 'user2',
        username: 'coach_mike',
        email: 'coach@example.com',
        role: 'coach'
      }
    },
    {
      id: '3',
      subject: 'Unable to create new team',
      message: 'The create team button is not working. I have a paid coach account but cannot create a team.',
      priority: 'urgent',
      status: 'open',
      category: 'team',
      createdAt: '2024-01-18T09:15:00Z',
      user: {
        id: 'user3',
        username: 'teamowner_sarah',
        email: 'sarah@example.com',
        role: 'team-owner'
      }
    }
  ]);

  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'high': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'urgent': return 'bg-red-500/10 text-red-400 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'in-progress': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'resolved': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'closed': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'account': return <Lock className="h-4 w-4" />;
      case 'team': return <Users className="h-4 w-4" />;
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      case 'technical': return <AlertCircle className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.user.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const ticketCounts = {
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    total: tickets.length
  };

  const handleSendResponse = () => {
    if (!responseMessage.trim() || !selectedTicket) return;
    
    // In a real app, this would make an API call
    console.log('Sending response:', {
      ticketId: selectedTicket.id,
      message: responseMessage,
      adminResponse: true
    });
    
    setResponseMessage('');
    // Update ticket status to in-progress if it was open
    // Refresh ticket data
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              onClick={onBack}
              variant="ghost"
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Support Center</h2>
            <p className="text-gray-400">Manage user support requests and tickets</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-500/10 border border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-sm font-medium">Open Tickets</p>
                <p className="text-2xl font-bold text-white">{ticketCounts.open}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-yellow-500/10 border border-yellow-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-400 text-sm font-medium">In Progress</p>
                <p className="text-2xl font-bold text-white">{ticketCounts.inProgress}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-500/10 border border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm font-medium">Resolved</p>
                <p className="text-2xl font-bold text-white">{ticketCounts.resolved}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-500/10 border border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm font-medium">Total Tickets</p>
                <p className="text-2xl font-bold text-white">{ticketCounts.total}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="lg:col-span-2">
          <Card className="bg-gray-900/50 border border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Support Tickets
              </CardTitle>
              
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Filter by priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedTicket?.id === ticket.id 
                      ? 'bg-sports-green/10 border-sports-green/30' 
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(ticket.category)}
                      <h3 className="text-white font-medium text-sm">{ticket.subject}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                      <Badge className={getStatusColor(ticket.status)}>
                        {ticket.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>@{ticket.user.username} ({ticket.user.role})</span>
                    <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                  </div>
                  
                  <p className="text-gray-300 text-sm mt-2 line-clamp-2">
                    {ticket.message}
                  </p>
                </div>
              ))}
              
              {filteredTickets.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No tickets match your current filters</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ticket Details */}
        <div className="lg:col-span-1">
          {selectedTicket ? (
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  {getCategoryIcon(selectedTicket.category)}
                  Ticket Details
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <h3 className="text-white font-medium mb-2">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={getPriorityColor(selectedTicket.priority)}>
                      {selectedTicket.priority}
                    </Badge>
                    <Badge className={getStatusColor(selectedTicket.status)}>
                      {selectedTicket.status}
                    </Badge>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-400 text-sm">User Information</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="text-white">@{selectedTicket.user.username}</p>
                    <p className="text-gray-400">{selectedTicket.user.email}</p>
                    <p className="text-gray-400">Role: {selectedTicket.user.role}</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-white font-medium mb-2">Original Message</h4>
                  <p className="text-gray-300 text-sm bg-gray-800 p-3 rounded">
                    {selectedTicket.message}
                  </p>
                </div>
                
                {/* Response Form */}
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-white font-medium mb-2">Send Response</h4>
                  <Textarea
                    placeholder="Type your response here..."
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mb-3"
                    rows={4}
                  />
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleSendResponse}
                      className="bg-sports-green text-black hover:bg-sports-green/90 flex-1"
                      disabled={!responseMessage.trim()}
                    >
                      <Reply className="h-4 w-4 mr-2" />
                      Send Response
                    </Button>
                    
                    <Button variant="outline" className="border-gray-600 text-gray-300">
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-900/50 border border-gray-700">
              <CardContent className="pt-6 text-center">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4 opacity-50" />
                <p className="text-gray-400">Select a ticket to view details and respond</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportCenter;